import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { 
  Bitcoin, 
  Shield,
  Users,
  Settings,
  FileText,
  TrendingUp,
  DollarSign,
  LogOut,
  ArrowLeft,
  Sliders,
  UserPlus,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import './AdminManagement.css';

const AdminManagement = () => {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('businesses');
  const [businesses, setBusinesses] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFeeModal, setShowFeeModal] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [feeSettings, setFeeSettings] = useState({
    platform_fee_percentage: 1.0,
    pass_fee_to_customer: false
  });
  const [agentForm, setAgentForm] = useState({
    email: '',
    password: '',
    business_name: '',
    revenue_share_percentage: 0
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, activeTab]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (activeTab === 'businesses') {
        const response = await axios.get(`${API}/admin/businesses`, { headers });
        setBusinesses(response.data);
      } else if (activeTab === 'agents') {
        const response = await axios.get(`${API}/admin/agents`, { headers });
        setAgents(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFeeModal = (business) => {
    setSelectedBusiness(business);
    setFeeSettings({
      platform_fee_percentage: business.platform_fee_percentage,
      pass_fee_to_customer: business.pass_fee_to_customer
    });
    setShowFeeModal(true);
  };

  const handleUpdateFees = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.put(
        `${API}/admin/business/${selectedBusiness.user_id}/fee-settings?platform_fee_percentage=${feeSettings.platform_fee_percentage}&pass_fee_to_customer=${feeSettings.pass_fee_to_customer}`,
        {},
        { headers }
      );
      
      toast.success('Fee settings updated');
      setShowFeeModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fees');
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/admin/create-agent`, agentForm, { headers });
      
      toast.success('Agent created successfully');
      setShowAgentModal(false);
      setAgentForm({
        email: '',
        password: '',
        business_name: '',
        revenue_share_percentage: 0
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-management-page">
      <header className="dashboard-header">
        <div className="header-container">
          <div className="header-left">
            <div className="logo">
              <Bitcoin size={32} />
              <span>Crypto Cashier Admin</span>
            </div>
          </div>
          <div className="header-right">
            <div className="admin-badge">
              <Shield size={16} />
              Admin
            </div>
            <div className="user-info">
              <span className="user-name">{user?.business_name}</span>
              <span className="user-email">{user?.email}</span>
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        <div className="dashboard-container">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} /> Back to Dashboard
          </button>

          <div className="page-header">
            <Shield size={32} />
            <h1>Platform Management</h1>
            <p>Manage businesses, agents, and fee structures</p>
          </div>

          {/* Tabs */}
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'businesses' ? 'active' : ''}`}
              onClick={() => setActiveTab('businesses')}
            >
              <Users size={18} />
              Businesses
            </button>
            <button 
              className={`tab ${activeTab === 'agents' ? 'active' : ''}`}
              onClick={() => setActiveTab('agents')}
            >
              <UserPlus size={18} />
              Agents
            </button>
            <button 
              className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              <TrendingUp size={18} />
              Analytics
            </button>
          </div>

          {/* Businesses Tab */}
          {activeTab === 'businesses' && (
            <div className="tab-content">
              <div className="businesses-table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Email</th>
                      <th>Fee %</th>
                      <th>Fee Paid By</th>
                      <th>Agent</th>
                      <th>Agreement</th>
                      <th>Revenue</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businesses.filter(b => !b.is_agent).map((business) => (
                      <tr key={business.user_id}>
                        <td className="business-name">{business.business_name}</td>
                        <td className="business-email">{business.email}</td>
                        <td>
                          <span className="fee-badge">{business.platform_fee_percentage}%</span>
                        </td>
                        <td>
                          <span className={`fee-type-badge ${business.pass_fee_to_customer ? 'customer' : 'merchant'}`}>
                            {business.pass_fee_to_customer ? 'Customer' : 'Merchant'}
                          </span>
                        </td>
                        <td>
                          {business.agent_id ? (
                            <span className="agent-tag">Via Agent</span>
                          ) : (
                            <span className="direct-tag">Direct</span>
                          )}
                        </td>
                        <td>
                          {business.agreement_signed ? (
                            <CheckCircle size={18} color="#22c55e" />
                          ) : (
                            <XCircle size={18} color="#ef4444" />
                          )}
                        </td>
                        <td className="revenue-cell">${business.platform_fees_accumulated?.toFixed(2) || '0.00'}</td>
                        <td>
                          <button 
                            className="btn-icon-small"
                            onClick={() => handleOpenFeeModal(business)}
                            title="Edit Fee Settings"
                          >
                            <Settings size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Agents Tab */}
          {activeTab === 'agents' && (
            <div className="tab-content">
              <div className="tab-header">
                <button 
                  className="btn btn-primary"
                  onClick={() => setShowAgentModal(true)}
                >
                  <UserPlus size={18} /> Create Agent
                </button>
              </div>

              <div className="agents-grid">
                {agents.map((agent) => (
                  <div key={agent.id} className="agent-card">
                    <div className="agent-header">
                      <h3>{agent.business_name}</h3>
                      <span className="revenue-share-badge">
                        {agent.agent_revenue_share_percentage}% Share
                      </span>
                    </div>
                    <p className="agent-email">{agent.email}</p>
                    <div className="agent-stats">
                      <div className="stat">
                        <span className="stat-label">Businesses:</span>
                        <span className="stat-value">
                          {businesses.filter(b => b.agent_id === agent.id).length}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total Revenue:</span>
                        <span className="stat-value">
                          ${businesses
                            .filter(b => b.agent_id === agent.id)
                            .reduce((sum, b) => sum + (b.platform_fees_accumulated || 0), 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="tab-content">
              <div className="analytics-grid">
                <div className="analytics-card">
                  <DollarSign size={32} color="#22c55e" />
                  <h3>Total Platform Revenue</h3>
                  <p className="analytics-value">
                    ${businesses.reduce((sum, b) => sum + (b.platform_fees_accumulated || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="analytics-card">
                  <Users size={32} color="#60a5fa" />
                  <h3>Active Businesses</h3>
                  <p className="analytics-value">{businesses.filter(b => !b.is_agent).length}</p>
                </div>
                <div className="analytics-card">
                  <UserPlus size={32} color="#a855f7" />
                  <h3>Active Agents</h3>
                  <p className="analytics-value">{agents.length}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fee Settings Modal */}
      {showFeeModal && (
        <div className="modal-overlay" onClick={() => setShowFeeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              <Sliders size={24} />
              Edit Fee Settings
            </h2>

            <div className="modal-body">
              <p className="business-name-modal">{selectedBusiness?.business_name}</p>

              <div className="form-group">
                <label>Platform Fee Percentage (1-3%)</label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={feeSettings.platform_fee_percentage}
                  onChange={(e) => setFeeSettings({...feeSettings, platform_fee_percentage: parseFloat(e.target.value)})}
                />
                <div className="range-value">{feeSettings.platform_fee_percentage.toFixed(1)}%</div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={feeSettings.pass_fee_to_customer}
                    onChange={(e) => setFeeSettings({...feeSettings, pass_fee_to_customer: e.target.checked})}
                  />
                  <span>Pass fee to customer</span>
                </label>
                <p className="help-text">
                  {feeSettings.pass_fee_to_customer 
                    ? 'Customer will pay the platform fee on top of product price'
                    : 'Merchant will pay the platform fee (deducted from payment)'}
                </p>
              </div>

              <div className="fee-example">
                <h4>Example:</h4>
                <p>Product: $100</p>
                <p>Platform Fee: ${(100 * feeSettings.platform_fee_percentage / 100).toFixed(2)}</p>
                <p className="example-result">
                  {feeSettings.pass_fee_to_customer ? (
                    <>Customer pays: ${(100 + (100 * feeSettings.platform_fee_percentage / 100)).toFixed(2)}<br/>Merchant receives: $100.00</>
                  ) : (
                    <>Customer pays: $100.00<br/>Merchant receives: ${(100 - (100 * feeSettings.platform_fee_percentage / 100)).toFixed(2)}</>
                  )}
                </p>
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowFeeModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateFees}>
                Update Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showAgentModal && (
        <div className="modal-overlay" onClick={() => setShowAgentModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              <UserPlus size={24} />
              Create Agent Account
            </h2>

            <form onSubmit={handleCreateAgent}>
              <div className="form-group">
                <label>Agent Name *</label>
                <input
                  type="text"
                  value={agentForm.business_name}
                  onChange={(e) => setAgentForm({...agentForm, business_name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={agentForm.email}
                  onChange={(e) => setAgentForm({...agentForm, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={agentForm.password}
                  onChange={(e) => setAgentForm({...agentForm, password: e.target.value})}
                  minLength="6"
                  required
                />
              </div>

              <div className="form-group">
                <label>Revenue Share % (0-100) *</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={agentForm.revenue_share_percentage}
                  onChange={(e) => setAgentForm({...agentForm, revenue_share_percentage: parseFloat(e.target.value)})}
                  required
                />
                <p className="help-text">
                  Agent will receive {agentForm.revenue_share_percentage}% of platform fees from their businesses
                </p>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAgentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Agent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
